/**
 * ArbitrationPanel Component
 *
 * Main integrated panel for dispute arbitration — full-height workspace shell:
 * top identity bar (back / type / title / status / live) + chat pane + context sidebar.
 *
 * Dependency Inversion: Depends on abstractions (services, hooks)
 * Single Responsibility: Orchestrates child components
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Backdrop,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  useTheme,
  alpha
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircleIcon from '@mui/icons-material/Circle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import GavelIcon from '@mui/icons-material/Gavel';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ArbitrationHeader from './ArbitrationHeader';
import ArbitrationChat from './ArbitrationChat';
import ArbitrationActions from './ArbitrationActions';
import {
  ApproveModal,
  RejectModal,
  RefundModal,
  ResolveModal,
  ChangeItemStatusModal,
  ForceDeliverModal,
  RequestRedeliveryModal,
  RefundInspectionFeeModal,
  OverrideInspectionModal,
  PenalizeInspectorModal
} from './modals';
import useArbitrationSocket from '../../hooks/useArbitrationSocket';
import arbitrationService, {
  IDisputeChat,
  IChatMessage,
  ArbitrationAction,
  DisputeType,
  DisputeStatus,
  StatusLabels,
  StatusColors,
  TypeLabels
} from '../../services/arbitrationService';
import { TYPE_HEX } from './constants';

interface ArbitrationPanelProps {
  disputeId?: string;
  onBack?: () => void;
}

/**
 * Modal state interface
 */
interface ModalState {
  open: boolean;
  action: ArbitrationAction | null;
  loading: boolean;
  error: string | null;
}

const TypeIcons: Record<DisputeType, React.ReactNode> = {
  [DisputeType.ORDER]: <ShoppingCartIcon sx={{ fontSize: 20 }} />,
  [DisputeType.ORDER_ITEM]: <InventoryIcon sx={{ fontSize: 20 }} />,
  [DisputeType.INSPECTION]: <SearchIcon sx={{ fontSize: 20 }} />
};

const ArbitrationPanel: React.FC<ArbitrationPanelProps> = ({
  disputeId: propDisputeId,
  onBack
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { chatId: paramChatId } = useParams<{ chatId: string }>();
  const [searchParams] = useSearchParams();

  // Determine dispute ID from props, params, or query string
  const disputeId = propDisputeId || paramChatId || searchParams.get('chatId') || '';

  // State
  const [dispute, setDispute] = useState<IDisputeChat | null>(null);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  // صفحه‌بندی پیام‌ها (چت‌های بیش از ۵۰ پیام قبلاً اصلاً قابل دیدن نبودند)
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());

  // Modal state
  const [modal, setModal] = useState<ModalState>({
    open: false,
    action: null,
    loading: false,
    error: null
  });

  // تب سایدبار: ۰ = اقدامات داوری، ۱ = پروندهٔ اختلاف
  const [sideTab, setSideTab] = useState(0);
  useEffect(() => {
    // برای پرونده‌های بسته‌شده اقدامی وجود ندارد — مستقیم تب پرونده
    if (dispute && [DisputeStatus.RESOLVED, DisputeStatus.REJECTED, DisputeStatus.CLOSED].includes(dispute.status)) {
      setSideTab(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispute?.status]);

  // Get current user ID from localStorage
  const currentUserId = localStorage.getItem('userId') || '';

  // Socket connection
  const {
    isConnected,
    isConnecting,
    typingUsers,
    sendTyping,
    sendStopTyping
  } = useArbitrationSocket({
    // Some disputes may not include underlying Chat _id immediately; allow joining by disputeId too.
    chatId: (dispute?.chatId || disputeId) || null,
    enabled: !!(dispute?.chatId || disputeId),
    onNewMessage: useCallback((message: IChatMessage) => {
      // Check for duplicates before adding
      setMessages(prev => {
        // Check if this exact message already exists
        const alreadyExists = prev.some(m => m.id === message.id);
        if (alreadyExists) {
          return prev;
        }

        // Check if there are temp messages (our own optimistic updates)
        const hasTemp = prev.some(m => m.id?.startsWith('temp_'));
        if (hasTemp) {
          // This might be our own message coming back from socket
          // Filter out temp messages and add the real one
          return [...prev.filter(m => !m.id?.startsWith('temp_')), message];
        }

        // New message from others, just add it
        return [...prev, message];
      });
    }, []),
    onUserJoined: useCallback((data: { userId: string; userName: string }) => {
      setOnlineUsers(prev => new Set([...Array.from(prev), data.userId]));
    }, []),
    onUserLeft: useCallback((data: { userId: string; userName?: string }) => {
      setOnlineUsers(prev => {
        const next = new Set(Array.from(prev));
        next.delete(data.userId);
        return next;
      });
    }, []),
    onChatClosed: useCallback(() => {
      setSnackbar({ open: true, message: 'چت داوری بسته شد', severity: 'info' as any });
      fetchDispute();
    }, [])
  });

  // Fetch dispute details
  const fetchDispute = useCallback(async () => {
    if (!disputeId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await arbitrationService.getDispute(disputeId);
      setDispute(data);
    } catch (err: any) {
      setError(err.message || 'خطا در دریافت اطلاعات اختلاف');
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  // Fetch messages (page 1 — جدیدترین ۵۰ پیام)
  const fetchMessages = useCallback(async () => {
    if (!disputeId) return;

    try {
      setMessagesLoading(true);
      const data = await arbitrationService.getMessages(disputeId, 1, 50);
      setMessages(data);
      setMessagesPage(1);
      setHasMoreMessages(data.length >= 50);
    } catch (err: any) {
      console.error('Error fetching messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, [disputeId]);

  // Load older messages (صفحات بعدی — prepend به ابتدای لیست)
  const loadOlderMessages = useCallback(async () => {
    if (!disputeId || loadingOlder) return;

    try {
      setLoadingOlder(true);
      const nextPage = messagesPage + 1;
      const older = await arbitrationService.getMessages(disputeId, nextPage, 50);
      if (older.length) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const fresh = older.filter(m => !existingIds.has(m.id));
          return [...fresh, ...prev];
        });
        setMessagesPage(nextPage);
      }
      setHasMoreMessages(older.length >= 50);
    } catch (err: any) {
      console.error('Error loading older messages:', err);
    } finally {
      setLoadingOlder(false);
    }
  }, [disputeId, messagesPage, loadingOlder]);

  // Initial load
  useEffect(() => {
    fetchDispute();
    fetchMessages();
  }, [fetchDispute, fetchMessages]);

  // Send message handler with optimistic update
  const handleSendMessage = async (content: string, imageUrl?: string) => {
    if (!disputeId) return;

    // Get current admin info for optimistic update
    const getCurrentAdmin = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return {
          id: user.id || user._id || currentUserId,
          name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || 'داور'
        };
      } catch {
        return { id: currentUserId, name: 'داور' };
      }
    };

    const admin = getCurrentAdmin();

    // Create optimistic message with temp ID for instant display
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const optimisticMessage: IChatMessage = {
      id: tempId,
      content: content,
      senderId: admin.id,
      senderName: admin.name,
      senderRole: 'admin',
      messageType: imageUrl ? 'image' : 'text',
      timestamp: new Date().toISOString(),
      isRead: false,
      ...(imageUrl && {
        file: {
          url: imageUrl,
          name: 'image',
          type: 'image/jpeg',
          size: 0
        }
      })
    };

    // Add optimistic message immediately for instant feedback
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      setSending(true);
      const message = await arbitrationService.sendMessage(disputeId, content, imageUrl);

      // Replace optimistic message with real message from server
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempId);
        // Check if real message already exists (avoid duplicates)
        const exists = filtered.some(m => m.id === message.id);
        return exists ? filtered : [...filtered, message];
      });
    } catch (err: any) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setSnackbar({ open: true, message: err.message || 'خطا در ارسال پیام', severity: 'error' });
      throw err;
    } finally {
      setSending(false);
    }
  };

  // Open action modal
  const handleOpenModal = (action: ArbitrationAction) => {
    setModal({
      open: true,
      action,
      loading: false,
      error: null
    });
  };

  // Close modal
  const handleCloseModal = () => {
    if (!modal.loading) {
      setModal({
        open: false,
        action: null,
        loading: false,
        error: null
      });
    }
  };

  // Execute action
  const handleExecuteAction = async (payload: any) => {
    if (!disputeId || !modal.action) {
      return;
    }

    try {
      setModal(prev => ({ ...prev, loading: true, error: null }));

      const result = await arbitrationService.executeAction(disputeId, modal.action, payload);

      if (result.success) {
        setSnackbar({ open: true, message: result.message || 'اقدام با موفقیت انجام شد', severity: 'success' });
        handleCloseModal();

        // Refresh dispute data
        await fetchDispute();
        await fetchMessages();
      } else {
        setModal(prev => ({ ...prev, error: result.message }));
      }
    } catch (err: any) {
      // ✅ FIX BUG-12: surface the backend's specific message (e.g. "تغییر وضعیت ... مجاز نیست")
      // instead of axios's generic "Request failed with status code 400".
      const backendMsg = err?.response?.data?.message || err?.message || 'خطا در انجام اقدام';
      setModal(prev => ({ ...prev, error: backendMsg }));
    } finally {
      setModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/dispute-center');
    }
  };

  // View user profile
  const handleViewProfile = (userId: string) => {
    window.open(`/users?userId=${userId}`, '_blank');
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // No dispute found
  if (!dispute) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        اختلاف مورد نظر یافت نشد
      </Alert>
    );
  }

  // Render modal based on action type
  const renderModal = () => {
    if (!modal.action) return null;

    const commonProps = {
      open: modal.open,
      onClose: handleCloseModal,
      onConfirm: handleExecuteAction,
      disputeTitle: dispute.title,
      loading: modal.loading,
      error: modal.error
    };

    switch (modal.action) {
      case ArbitrationAction.APPROVE:
        return <ApproveModal {...commonProps} disputeType={dispute.type} />;

      case ArbitrationAction.REJECT:
        return <RejectModal {...commonProps} />;

      case ArbitrationAction.REFUND_ITEM:
      case ArbitrationAction.REFUND_ORDER:
        return (
          <RefundModal
            {...commonProps}
            financial={dispute.financial}
            isOrderItem={modal.action === ArbitrationAction.REFUND_ITEM}
          />
        );

      case ArbitrationAction.RESOLVE:
        return <ResolveModal {...commonProps} disputeType={dispute.type} />;

      case ArbitrationAction.CHANGE_ITEM_STATUS:
        return (
          <ChangeItemStatusModal
            {...commonProps}
            currentStatus={dispute.itemStatus}
          />
        );

      case ArbitrationAction.FORCE_DELIVER:
        return <ForceDeliverModal {...commonProps} />;

      case ArbitrationAction.REQUEST_REDELIVERY:
        return <RequestRedeliveryModal {...commonProps} />;

      // Inspection actions
      case ArbitrationAction.REFUND_INSPECTION_FEE:
        return <RefundInspectionFeeModal {...commonProps} financial={dispute.financial} />;

      case ArbitrationAction.OVERRIDE_INSPECTION:
        return (
          <OverrideInspectionModal
            {...commonProps}
            currentInspectionStatus={dispute.reference?.inspectionStatus}
          />
        );

      case ArbitrationAction.PENALIZE_INSPECTOR:
        return <PenalizeInspectorModal {...commonProps} />;

      default:
        return null;
    }
  };

  const typeHex = TYPE_HEX[dispute.type] || '#0ea5e9';
  const connectionOk = isConnected;

  return (
    <Box
      sx={{
        px: { xs: 0.75, sm: 1.5 },
        py: { xs: 0.75, sm: 1.25 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        height: { md: 'calc(100vh - 84px)' },
        minHeight: { md: 560 },
        bgcolor: 'background.default'
      }}
    >
      {/* ── Top identity bar ─────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          px: 1.25,
          py: 1,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}
      >
        <Tooltip title="بازگشت">
          <IconButton size="small" onClick={handleBack}>
            <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(typeHex, isDark ? 0.25 : 0.12),
            color: typeHex,
            flexShrink: 0
          }}
        >
          {TypeIcons[dispute.type]}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {dispute.title}
          </Typography>
          <Typography variant="caption" sx={{ color: typeHex, fontWeight: 700 }}>
            {TypeLabels[dispute.type]}
            <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>
              {' '}· داوری اختلاف
            </Box>
          </Typography>
        </Box>

        <Chip
          label={StatusLabels[dispute.status]}
          color={StatusColors[dispute.status]}
          size="small"
          sx={{ fontWeight: 700, flexShrink: 0 }}
        />

        {dispute.chatId && (
          <Chip
            size="small"
            variant={connectionOk ? 'filled' : 'outlined'}
            color={connectionOk ? 'success' : isConnecting ? 'warning' : 'default'}
            icon={
              <CircleIcon
                sx={{
                  fontSize: '9px !important',
                  ...(connectionOk && {
                    animation: 'arbPulse 1.6s ease-in-out infinite',
                    '@keyframes arbPulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.35 }
                    }
                  })
                }}
              />
            }
            label={connectionOk ? 'گفتگوی زنده' : isConnecting ? 'در حال اتصال…' : 'آفلاین'}
            sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
          />
        )}

        <Tooltip title="بروزرسانی">
          <span>
            <IconButton size="small" onClick={fetchDispute} disabled={loading}>
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  animation: loading ? 'arbSpin 1s linear infinite' : 'none',
                  '@keyframes arbSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Paper>

      {/* ── Workspace: chat + context sidebar ────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.25,
          alignItems: 'stretch'
        }}
      >
        {/* Chat pane (primary work surface) */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            height: { xs: '62vh', md: '100%' },
            borderRadius: 2.5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {dispute.chatId ? (
            <ArbitrationChat
              messages={messages}
              participants={dispute.participants}
              loading={messagesLoading}
              sending={sending}
              typingUsers={typingUsers}
              onSendMessage={handleSendMessage}
              onTyping={sendTyping}
              onStopTyping={sendStopTyping}
              currentUserId={currentUserId}
              disabled={[DisputeStatus.RESOLVED, DisputeStatus.REJECTED, DisputeStatus.CLOSED].includes(dispute.status)}
              hasMoreMessages={hasMoreMessages}
              loadingOlder={loadingOlder}
              onLoadOlder={loadOlderMessages}
              onlineUsers={onlineUsers}
              onViewProfile={handleViewProfile}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 2 }}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                چت داوری هنوز ایجاد نشده است. برای شروع داوری، اختلاف را تأیید کنید.
              </Alert>
            </Box>
          )}
        </Paper>

        {/* Context sidebar: تب «اقدامات داوری» + تب «پروندهٔ اختلاف» — هر دو یک‌کلیک دم دست، بدون اسکرول اضافه */}
        <Paper
          variant="outlined"
          sx={{
            width: { xs: '100%', md: 352 },
            flexShrink: 0,
            height: { md: '100%' },
            borderRadius: 2.5,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            mb: { xs: 1, md: 0 }
          }}
        >
          <Tabs
            value={sideTab}
            onChange={(_e, v) => setSideTab(v)}
            variant="fullWidth"
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              minHeight: 44,
              flexShrink: 0,
              bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#64748b', 0.04),
              '& .MuiTab-root': {
                minHeight: 44,
                fontSize: 12.5,
                fontWeight: 700,
                textTransform: 'none'
              }
            }}
          >
            <Tab icon={<GavelIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="اقدامات داوری" />
            <Tab icon={<DescriptionIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="پروندهٔ اختلاف" />
          </Tabs>

          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 300, md: 0 },
              maxHeight: { xs: 440, md: 'none' },
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(theme.palette.text.primary, 0.15),
                borderRadius: 3
              }
            }}
          >
            <Box sx={{ display: sideTab === 0 ? 'block' : 'none' }}>
              <ArbitrationActions
                availableActions={dispute.availableActions}
                disputeType={dispute.type}
                disputeStatus={dispute.status}
                onAction={handleOpenModal}
                loading={modal.loading}
                embedded
              />
            </Box>
            <Box sx={{ display: sideTab === 1 ? 'block' : 'none' }}>
              <ArbitrationHeader dispute={dispute} loading={loading} embedded />
            </Box>
          </Box>
          {/* Note: Postal-return management lives in OrderManagement (مدیریت سفارشات). */}
        </Paper>
      </Box>

      {/* Modals */}
      {renderModal()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Loading Backdrop */}
      <Backdrop open={modal.loading} sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default ArbitrationPanel;
